import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, ShieldCheck, BarChart3, AlarmClock, Fuel } from "lucide-react";

export default function SiteSettingsPage() {
  const [form, setForm] = useState({
    rni_number: "", rni_status: "Applied", print_masthead: "",
    publisher: "JAKIR MOMIN", printer: "", owner: "JAKIR MOMIN",
    analytics_code: "", daily_digest_enabled: false, weekly_digest_enabled: false, auto_tweet_enabled: false, auto_fb_post_enabled: false, briefing_push_enabled: false, adsense_client_id: "",
    adsense_slot_article: "", whatsapp_channel_url: "", data_gov_key: "",
    emergency_banner_active: false, emergency_banner_message: "", emergency_banner_level: "warning", emergency_banner_link: "",
  });
  const [fuel, setFuel] = useState({ city: "सूरजपुर, छत्तीसगढ़", petrol: "", diesel: "", cng: "", lpg: "", source: "स्थानीय पंप" });
  const [busy, setBusy] = useState(false);
  const [fuelBusy, setFuelBusy] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setForm((s) => ({
      ...s,
      rni_number: r.data.rni_number || "",
      rni_status: r.data.rni_status || "Applied",
      print_masthead: r.data.print_masthead || "",
      publisher: r.data.publisher || "JAKIR MOMIN",
      printer: r.data.printer || "",
      owner: r.data.owner || "JAKIR MOMIN",
      analytics_code: r.data.analytics_code || "",
      daily_digest_enabled: !!r.data.daily_digest_enabled,
      weekly_digest_enabled: !!r.data.weekly_digest_enabled,
      auto_tweet_enabled: !!r.data.auto_tweet_enabled,
      auto_fb_post_enabled: !!r.data.auto_fb_post_enabled,
      briefing_push_enabled: !!r.data.briefing_push_enabled,
      adsense_client_id: r.data.adsense_client_id || "",
      adsense_slot_article: r.data.adsense_slot_article || "",
      whatsapp_channel_url: r.data.whatsapp_channel_url || "",
      data_gov_key: r.data.data_gov_key || "",
      emergency_banner_active: !!r.data.emergency_banner_active,
      emergency_banner_message: r.data.emergency_banner_message || "",
      emergency_banner_level: r.data.emergency_banner_level || "warning",
      emergency_banner_link: r.data.emergency_banner_link || "",
    })));
    api.get("/fuel-prices").then((r) => {
      const d = r.data || {};
      setFuel((s) => ({
        ...s,
        city: d.city || s.city,
        petrol: d.petrol ?? "",
        diesel: d.diesel ?? "",
        cng: d.cng ?? "",
        lpg: d.lpg ?? "",
        source: d.source || s.source,
      }));
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/site-settings", form); toast.success("सेटिंग्स सहेजी गयीं"); }
    catch(err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const saveFuel = async (e) => {
    e.preventDefault(); setFuelBusy(true);
    try {
      const payload = {
        city: fuel.city || null,
        petrol: fuel.petrol === "" ? null : parseFloat(fuel.petrol),
        diesel: fuel.diesel === "" ? null : parseFloat(fuel.diesel),
        cng: fuel.cng === "" ? null : parseFloat(fuel.cng),
        lpg: fuel.lpg === "" ? null : parseFloat(fuel.lpg),
        source: fuel.source || null,
      };
      await api.put("/admin/fuel-prices", payload);
      toast.success("ईंधन के दाम अपडेट हुए");
    } catch(err) { toast.error(formatError(err)); }
    finally { setFuelBusy(false); }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">साइट सेटिंग्स</h1>
        <p className="text-sm text-slate-500 mt-1">प्रकाशन जानकारी, ट्रैकिंग और स्वचालन</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Masthead */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><ShieldCheck className="w-5 h-5 text-[#1E3A8A]"/>प्रिंट मास्टहेड / RNI विवरण</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>RNI Registration नंबर</Label><Input value={form.rni_number} onChange={(e) => setForm({...form, rni_number: e.target.value})} placeholder="जैसे: CHHHIN/2026/xxxxx" data-testid="settings-rni"/></div>
            <div><Label>RNI Status</Label><Input value={form.rni_status} onChange={(e) => setForm({...form, rni_status: e.target.value})} placeholder="Applied / Registered"/></div>
          </div>
          <div><Label>Print Masthead / Publication Line</Label><Input value={form.print_masthead} onChange={(e) => setForm({...form, print_masthead: e.target.value})} placeholder="जैसे: Volume 1, Issue 1, Published Weekly"/></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>प्रकाशक (Publisher)</Label><Input value={form.publisher} onChange={(e) => setForm({...form, publisher: e.target.value})} data-testid="settings-publisher"/></div>
            <div><Label>स्वामी (Owner)</Label><Input value={form.owner} onChange={(e) => setForm({...form, owner: e.target.value})}/></div>
            <div><Label>मुद्रक (Printer)</Label><Input value={form.printer} onChange={(e) => setForm({...form, printer: e.target.value})} placeholder="वैकल्पिक"/></div>
          </div>
        </div>

        {/* Analytics */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><BarChart3 className="w-5 h-5 text-emerald-600"/>ट्रैफ़िक विश्लेषण (Google Analytics / Umami)</div>
          <p className="text-xs text-slate-500">अपना GA/Umami/GTM का पूरा snippet यहाँ paste करें — यह हर पेज पर <code>&lt;head&gt;</code> में inject होगा।</p>
          <Textarea rows={5} value={form.analytics_code} onChange={(e) => setForm({...form, analytics_code: e.target.value})}
            placeholder="जैसे: <script async src='https://www.googletagmanager.com/gtag/js?id=G-XXXXX'></script>..."
            className="font-mono text-xs" data-testid="settings-analytics"/>
        </div>

        {/* Google AdSense */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><BarChart3 className="w-5 h-5 text-orange-500"/>Google AdSense</div>
          <p className="text-xs text-slate-500">AdSense approval मिलने के बाद यहाँ Publisher ID + In-Article Ad Slot ID डालें।</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Publisher ID (Client)</Label><Input value={form.adsense_client_id} onChange={(e) => setForm({...form, adsense_client_id: e.target.value})} placeholder="ca-pub-XXXXXXXXXXXXXXXX" className="font-mono text-sm" data-testid="settings-adsense"/></div>
            <div><Label>In-Article Ad Slot ID</Label><Input value={form.adsense_slot_article} onChange={(e) => setForm({...form, adsense_slot_article: e.target.value})} placeholder="1234567890" className="font-mono text-sm" data-testid="settings-adsense-slot"/></div>
          </div>
        </div>

        {/* WhatsApp Channel */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><BarChart3 className="w-5 h-5 text-emerald-600"/>WhatsApp Channel</div>
          <p className="text-xs text-slate-500">अपना WhatsApp Channel URL डालें — Admin panel में "WhatsApp पर भेजें" button पर shortcut मिलेगा।</p>
          <Input value={form.whatsapp_channel_url} onChange={(e) => setForm({...form, whatsapp_channel_url: e.target.value})} placeholder="https://whatsapp.com/channel/XXXX" data-testid="settings-wa-channel"/>
        </div>

        {/* AgMarknet data.gov.in key */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><BarChart3 className="w-5 h-5 text-green-700"/>AgMarknet API Key (data.gov.in)</div>
          <p className="text-xs text-slate-500">Free API key से रोज़ mandi rates auto-refresh हो सकते हैं। यह key server पर सुरक्षित रूप से save होती है और public API me expose नहीं होती।</p>
          <Input value={form.data_gov_key} onChange={(e) => setForm({...form, data_gov_key: e.target.value})}
            placeholder="579b464db66ec23bdd0000..." className="font-mono text-xs" type="password" data-testid="settings-datagov"/>
        </div>

        {/* Automation */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><AlarmClock className="w-5 h-5 text-[#EA580C]"/>स्वचालन (Automation)</div>
          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50">
            <Switch checked={form.daily_digest_enabled} onCheckedChange={(v) => setForm({...form, daily_digest_enabled: v})} data-testid="settings-digest-auto"/>
            <div>
              <div className="font-medium text-slate-800">रोज़ सुबह 7:00 बजे स्वचालित न्यूज़लेटर भेजें</div>
              <div className="text-xs text-slate-500 mt-0.5">IST · सभी सक्रिय सब्सक्राइबर्स को टॉप-5 डाइजेस्ट अपने आप जायेगा</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50">
            <Switch checked={form.briefing_push_enabled} onCheckedChange={(v) => setForm({...form, briefing_push_enabled: v})} data-testid="settings-briefing-push"/>
            <div>
              <div className="font-medium text-slate-800">रोज़ सुबह 7:00 बजे "Morning Briefing" पुश सूचना भेजें</div>
              <div className="text-xs text-slate-600 mt-0.5">🌅 मौसम + AQI + पेट्रोल/डीज़ल के दाम — सभी पुश सब्सक्राइबर्स के फ़ोन पर auto pop-up</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-md border border-purple-200 bg-purple-50">
            <Switch checked={form.weekly_digest_enabled} onCheckedChange={(v) => setForm({...form, weekly_digest_enabled: v})} data-testid="settings-weekly-digest"/>
            <div className="flex-1">
              <div className="font-medium text-slate-800 flex items-center gap-2">
                हर रविवार सुबह 8:00 बजे साप्ताहिक डाइजेस्ट भेजें
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/preview-weekly`} target="_blank" rel="noopener noreferrer"
                  className="ml-1 text-[10px] uppercase tracking-widest bg-purple-600 text-white px-2 py-0.5 rounded-full hover:bg-purple-700"
                  data-testid="weekly-digest-preview">Preview</a>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">📧 इस हफ़्ते की टॉप 5 खबरें + JAKIR MOMIN की एडिटर कॉलम + AQI/ईंधन सारांश</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-md border border-sky-200 bg-sky-50">
            <Switch checked={form.auto_tweet_enabled} onCheckedChange={(v) => setForm({...form, auto_tweet_enabled: v})} data-testid="settings-auto-tweet"/>
            <div className="flex-1">
              <div className="font-medium text-slate-800 flex items-center gap-2">
                Twitter/X auto-post हर published news पर
                <button type="button" onClick={async () => { try { const r = await api.post('/admin/tweet/test'); toast.success(r.data.ok ? '✅ Test tweet posted' : `Stub mode (${r.data.missing || 'not configured'})`); } catch { toast.error('Test failed'); } }}
                  className="ml-1 text-[10px] uppercase tracking-widest bg-sky-600 text-white px-2 py-0.5 rounded-full hover:bg-sky-700" data-testid="test-tweet-btn">Test</button>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">🐦 हर बार जब news publish होगा (या reporter submission approve होगा), title + link + hero photo automatic @INDIAN_NEWS_21 pe tweet होगा। Stub mode जब तक Access Token Secret .env में add नहीं होता।</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-md border border-blue-300 bg-blue-50">
            <Switch checked={form.auto_fb_post_enabled} onCheckedChange={(v) => setForm({...form, auto_fb_post_enabled: v})} data-testid="settings-auto-fb"/>
            <div className="flex-1">
              <div className="font-medium text-slate-800 flex items-center gap-2">
                Facebook Page auto-post हर published news पर
                <button type="button" onClick={async () => { try { const r = await api.post('/admin/facebook/test'); toast.success(r.data.ok ? '✅ Test post gaya Facebook' : `Stub mode (${r.data.missing || r.data.error || 'not configured'})`); } catch { toast.error('Test failed'); } }}
                  className="ml-1 text-[10px] uppercase tracking-widest bg-blue-600 text-white px-2 py-0.5 rounded-full hover:bg-blue-700" data-testid="test-fb-btn">Test</button>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">📘 Title + excerpt + link + hero photo automatic INDIAN-NEWS-21 Facebook Page पर publish होगा। Stub mode जब तक <code className="bg-slate-200 px-1 rounded">FB_PAGE_ID</code> + <code className="bg-slate-200 px-1 rounded">FB_PAGE_ACCESS_TOKEN</code> .env में add नहीं होते।</div>
            </div>
          </label>
        </div>
        <div className="bg-white border-2 border-red-200 rounded-lg p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><BarChart3 className="w-5 h-5 text-red-600"/>आपात प्रसारण बैनर</div>
          <p className="text-xs text-slate-500">Cyclone, बाढ़, civil defence alerts जैसी आपात सूचना पूरी वेबसाइट के top पर लाल/पीले बैनर के रूप में दिखेगी।</p>
          <label className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-red-50">
            <Switch checked={form.emergency_banner_active} onCheckedChange={(v) => setForm({...form, emergency_banner_active: v})} data-testid="settings-emergency-active"/>
            <div>
              <div className="font-medium text-slate-800">बैनर सक्रिय करें</div>
              <div className="text-xs text-slate-600 mt-0.5">Toggle on करते ही बैनर लाइव हो जायेगा; toggle off करने पर तुरंत हट जायेगा।</div>
            </div>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label>संदेश (हिंदी)</Label>
              <Input value={form.emergency_banner_message} onChange={(e) => setForm({...form, emergency_banner_message: e.target.value})}
                placeholder="जैसे: बाढ़ चेतावनी — सूरजपुर में भारी बारिश की संभावना, सतर्क रहें।" data-testid="settings-emergency-msg"/>
            </div>
            <div>
              <Label>स्तर</Label>
              <select value={form.emergency_banner_level} onChange={(e) => setForm({...form, emergency_banner_level: e.target.value})}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="settings-emergency-level">
                <option value="info">Info · नीला</option>
                <option value="warning">Warning · पीला</option>
                <option value="danger">Danger · लाल</option>
              </select>
            </div>
          </div>
          <div>
            <Label>विवरण लिंक (वैकल्पिक)</Label>
            <Input value={form.emergency_banner_link} onChange={(e) => setForm({...form, emergency_banner_link: e.target.value})}
              placeholder="/article/... या सरकारी NDMA URL" data-testid="settings-emergency-link"/>
          </div>
        </div>

        <Button type="submit" disabled={busy} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="save-settings">
          <Save className="w-4 h-4 mr-1.5"/>{busy ? "सहेज रहे…" : "सेटिंग्स सहेजें"}
        </Button>
      </form>

      {/* Fuel Prices (separate form to avoid conflicts) */}
      <form onSubmit={saveFuel} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-slate-800 font-bold"><Fuel className="w-5 h-5 text-rose-600"/>आज के ईंधन के दाम</div>
        <p className="text-xs text-slate-500">रोज़ ताज़ा दाम डालिये — ये होमपेज पर दिखेंगे। खाली छोड़ने पर वो field छिप जायेगी।</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>शहर / क्षेत्र</Label><Input value={fuel.city} onChange={(e) => setFuel({...fuel, city: e.target.value})} data-testid="fuel-city"/></div>
          <div><Label>स्रोत (Source)</Label><Input value={fuel.source} onChange={(e) => setFuel({...fuel, source: e.target.value})} placeholder="जैसे: स्थानीय पंप, IOCL" data-testid="fuel-source"/></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><Label>पेट्रोल (₹/L)</Label><Input type="number" step="0.01" value={fuel.petrol} onChange={(e) => setFuel({...fuel, petrol: e.target.value})} placeholder="102.50" data-testid="fuel-petrol"/></div>
          <div><Label>डीज़ल (₹/L)</Label><Input type="number" step="0.01" value={fuel.diesel} onChange={(e) => setFuel({...fuel, diesel: e.target.value})} placeholder="93.20" data-testid="fuel-diesel"/></div>
          <div><Label>CNG (₹/kg)</Label><Input type="number" step="0.01" value={fuel.cng} onChange={(e) => setFuel({...fuel, cng: e.target.value})} placeholder="78.00" data-testid="fuel-cng"/></div>
          <div><Label>LPG 14.2kg (₹)</Label><Input type="number" step="0.01" value={fuel.lpg} onChange={(e) => setFuel({...fuel, lpg: e.target.value})} placeholder="905.00" data-testid="fuel-lpg"/></div>
        </div>
        <Button type="submit" disabled={fuelBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="save-fuel">
          <Save className="w-4 h-4 mr-1.5"/>{fuelBusy ? "सहेज रहे…" : "ईंधन दाम सहेजें"}
        </Button>
      </form>
    </div>
  );
}
